/// <amd-module name="Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions" />
import * as React from "react";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { htmlEncode } from "VSS/Features/Markdown/Utils/UrlHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { ListSelection } from "VSSUI/List";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { Tooltip } from "VSSUI/TooltipEx";
import { getSafeId } from "VSSUI/Util";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";
import { IIdentity, Persona, PersonaSize } from "VSSUI/Persona";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { convertValueToDisplayString } from "Wit/Common/QueryUtils/QueryUtils";
import { getWorkItemEditUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import * as Resources from "Wit/Common/Utils/WorkItemUtils/Resources";
import { ChangeTypeDialog } from "Wit/Common/WorkItemDialogs/Components/ChangeTypeDialog";
import { showConfirmationDialog } from "Wit/Common/WorkItemDialogs/Components/ConfirmationDialog";
import { EmailWorkItemsDialog } from "Wit/Common/WorkItemDialogs/Components/EmailWorkItemsDialog";
import { MoveToPositionDialog } from "Wit/Common/WorkItemDialogs/Components/MoveToPositionDialog";
import { MoveToProjectDialog } from "Wit/Common/WorkItemDialogs/Components/MoveToProjectDialog";
import { ProcessWorkItemsDialog, restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { getWorkItemFieldService, IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { getWorkItemFieldService as getWorkItemFieldService2, IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { convertPotentialIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { getUnassignedIdentityRef } from "Wit/UI/IdentityView";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { INewLinkData, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { htmlDecode } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { Link, WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { getRelatedWorkLinkTypeFilter, getRelatedWorkLinkableWorkItemTypeFilter, getRelatedWorkNewLinkTypeFilter } from "./LinkTypeFilter";
import { RecycleBinRequestType, RecycleBinWorkItemsDialog } from "./RecycleBinWorkItemsDialog";

interface IColumn {
    name?: string;
    text?: string;
    isIdentity?: boolean;
}

export const TestItemTypes = ["Test Case", "Test Suite", "Test Plan", "Shared Steps", "Shared Parameter"];

export const getChangeTypeCommandMenuItem = (
    pageContext: IVssPageContext,
    groupKey: string,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    disabled?: boolean,
    onModifyItems?: (items: IWorkItem[]) => void,
    projectId?: string
): IMenuItem => {
    return {
        disabled: disabled,
        rank: 11,
        id: "change-work-item-type",
        text: Resources.ChangeType,
        iconProps: { iconName: "Switch" },
        groupKey: groupKey,
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, items: INamedFieldDataDictionary[]) => {
                openDialog(pageContext, onDismiss => (
                    <ChangeTypeDialog
                        onDismiss={onDismiss}
                        onModifyItems={onModifyItems}
                        selectedItems={items}
                        workItemProjectId={projectId}
                    />
                ));
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
};

export const getCopyAsHTMLCommandMenuItem = (
    pageContext: IVssPageContext,
    groupKey: string,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    columns: IColumn[]
) => {
    return {
        rank: 20,
        id: "copy-html",
        text: Resources.CopyWorkItemsAsHtml,
        iconProps: { iconName: "Copy" },
        groupKey: groupKey,
        onActivate: () => {
            onCopyHTML(pageContext, getSelectedItems, columns);
        }
    };
};

export const getCopyLinkCommandMenuItem = (
    pageContext: IVssPageContext,
    groupKey: string,
    getSelectedItems: () => INamedFieldDataDictionary[],
    disabled?: boolean
) => ({
    rank: 21,
    disabled: disabled,
    id: "copy-link",
    text: Resources.CopyLink,
    iconProps: { iconName: "Link" },
    groupKey: groupKey,
    onActivate: () => {
        onCopyLink(pageContext, getSelectedItems);
    }
});

export const getEmailCommandMenuItem = (
    pageContext: IVssPageContext,
    columns: IColumn[],
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    getTempQueryId?: () => Promise<string>,
    groupKey?: string | undefined,
    emailSubject?: string
): IMenuItem => {
    const getDefaultEmailSubject = (selectedItems: INamedFieldDataDictionary[]) => {
        if (selectedItems.length > 1) {
            return format(Resources.WorkItemCount, selectedItems.length);
        } else {
            return format(Resources.WorkItemSubject, selectedItems[0][CoreFieldRefNames.Id]);
        }
    };

    return {
        rank: 71,
        id: "email-selection",
        text: Resources.EmailSelectedWorkItems,
        iconProps: { iconName: "Mail" },
        groupKey: groupKey ?? "export",
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, items: INamedFieldDataDictionary[]) => {
                openDialog(pageContext, onDismiss => (
                    <EmailWorkItemsDialog
                        columnFieldNames={columns.map(column => column.text).filter(v => v) as string[]}
                        referenceFieldNames={columns.map(column => column.name).filter(v => v) as string[]}
                        onDismiss={onDismiss}
                        selectedItems={items}
                        defaultSubject={emailSubject ?? getDefaultEmailSubject(items)}
                        getTempQueryId={getTempQueryId}
                    />
                ));
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
};

const getLinkToExistingData = async (
    pageContext: IVssPageContext,
    selectedItems: INamedFieldDataDictionary[],
): Promise<{ newLinks: Link[], workItems: IWorkItem[] }> => {
    let newLinks: Link[] = [];
    const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const workItemIds = selectedItems.map(item => item[CoreFieldRefNames.Id]);

    const workItems = await workItemService.getWorkItems(workItemIds);
    await new Promise(resolve => {
        openDialog(pageContext, onInnerDismiss => {
            const dismissFunction = () => {
                onInnerDismiss();
                resolve(undefined);
            };
            return (
                <WrappedComponent
                    dependencies={[
                        "ms.vss-work-web.work-item-form-view",
                        "ms.vss-work-web.work-item-form-dependencies",
                        "ms.vss-work-web.new-work-item-form-content"
                    ]}
                    wrappedType="add-link-dialog"
                    originWorkItems={workItems}
                    linkTypeFilter={getRelatedWorkLinkTypeFilter(workItems)}
                    linkableWorkItemTypeFilter={getRelatedWorkLinkableWorkItemTypeFilter(workItems)}
                    onAddLinks={(newLinkDisplayData: IResolvedLinkDisplayData[]) => {
                        newLinks = newLinkDisplayData.map(d => Link.createFromLinkData(d.linkIdentifier));

                        dismissFunction();
                    }}
                    onDismiss={dismissFunction}
                />
            );
        });
    });

    return { newLinks, workItems };
}

export const getLinkToExistingCommandMenuItem = (
    pageContext: IVssPageContext,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    customSaveLogic?: (workItems: IWorkItem[], newLinks: Link[]) => Promise<void>
): IMenuItem => {
    return {
        rank: 50,
        id: "link-to-existing",
        text: Resources.LinkToExistingItem,
        iconProps: { iconName: "Link" },
        groupKey: "link",
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, selectedItems: INamedFieldDataDictionary[]) => {
                getLinkToExistingData(pageContext, selectedItems).then(async (data) => {
                    if (customSaveLogic) {
                        await customSaveLogic(data.workItems, data.newLinks);
                    } else {
                        for (let workItem of data.workItems) {
                            workItem.addLinks(data.newLinks);
                        }
                        const workItemFormSavingService = pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
                        workItemFormSavingService.beginSaveWorkItemsBatch(data.workItems, () => { } /*onDismiss*/);
                    }
                });
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
};

///obsolete, use getLinkToExistingCommandMenuItem instead
export const getLinkToExistingCommandMenuItemOld = (
    pageContext: IVssPageContext,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[]
): IMenuItem => {
    return {
        rank: 50,
        id: "link-to-existing",
        text: Resources.LinkToExistingItem,
        iconProps: { iconName: "Link" },
        groupKey: "link",
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, selectedItems: INamedFieldDataDictionary[]) => {
                const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
                const workItemIds = selectedItems.map(item => item[CoreFieldRefNames.Id]);

                workItemService.getWorkItems(workItemIds).then(workItems => {
                    openDialog(pageContext, onDismiss => {
                        return (
                            <WrappedComponent
                                dependencies={[
                                    "ms.vss-work-web.work-item-form-view",
                                    "ms.vss-work-web.work-item-form-dependencies",
                                    "ms.vss-work-web.new-work-item-form-content"
                                ]}
                                wrappedType="add-link-dialog"
                                originWorkItems={workItems}
                                linkTypeFilter={getRelatedWorkLinkTypeFilter(workItems)}
                                linkableWorkItemTypeFilter={getRelatedWorkLinkableWorkItemTypeFilter(workItems)}
                                onAddLinks={(newLinkDisplayData: IResolvedLinkDisplayData[]) => {
                                    const newLinks = newLinkDisplayData.map(d => Link.createFromLinkData(d.linkIdentifier));

                                    for (let workItem of workItems) {
                                        workItem.addLinks(newLinks);
                                    }
                                    const workItemFormSavingService = pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");

                                    workItemFormSavingService.beginSaveWorkItemsBatch(workItems, onDismiss);

                                }}
                                onDismiss={onDismiss}
                            />
                        );
                    });
                });
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
}

export const getLinkToNewCommandMenuItem = (
    pageContext: IVssPageContext,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    projectId: string): IMenuItem => {
    return {
        rank: 10,
        id: "link-to-new",
        text: Resources.LinkSelectedItemsToNewWorkItem,
        iconProps: { iconName: "WorkItem" },
        groupKey: "link",
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, selectedItems: INamedFieldDataDictionary[]) => {
                const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
                const workItemIds = selectedItems.map(item => item[CoreFieldRefNames.Id]);
                workItemService.getWorkItems(workItemIds).then(workItems => {
                    openDialog(pageContext, onDismiss => {
                        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                        return (
                            <WrappedComponent
                                dependencies={[
                                    "ms.vss-work-web.work-item-form-view",
                                    "ms.vss-work-web.work-item-form-dependencies",
                                    "ms.vss-work-web.new-work-item-form-content"
                                ]}
                                wrappedType="add-link-dialog"
                                originWorkItems={workItems}
                                linkTypeFilter={getRelatedWorkNewLinkTypeFilter(workItems)}
                                linkableWorkItemTypeFilter={getRelatedWorkLinkableWorkItemTypeFilter(workItems)}
                                onNewLinksAdded={async (newLinkData: { newWorkItemData: INewLinkData; linkTypeName: string; comment: string; assignedTo?: string }) => {
                                    if (!projectId) {
                                        return;
                                    }
                                    const item = await workItemService.createWorkItem(newLinkData.newWorkItemData.typeData, projectId);
                                    const linkCreationPromises = workItems.map(workItem =>
                                        WorkItemLink.create(pageContext, newLinkData.linkTypeName, workItem.id, newLinkData.comment)
                                    );
                                    const workItemLinks = await Promise.all(linkCreationPromises);
                                    item.addLinks(workItemLinks);
                                    item.setFieldValueByName(CoreFieldRefNames.Title, newLinkData.newWorkItemData.title);
                                    item.setFieldValueByName(CoreFieldRefNames.AssignedTo, newLinkData.assignedTo);
                                    layoutManager.renderCallout(onInnerDismiss => {
                                        const dismissFunction = () => {
                                            onInnerDismiss();
                                            onDismiss();
                                        };
                                        return <WorkItemFormDialog workItem={item} onDismiss={dismissFunction} />;
                                    });
                                }}
                                onDismiss={onDismiss}
                                isLinkToNewItem
                            />
                        );
                    });
                });
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
};

export const getMoveToTeamProjectCommandMenuItem = (
    pageContext: IVssPageContext,
    groupKey: string,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    onModifyItems?: (items: IWorkItem[]) => void
): IMenuItem => {
    return {
        rank: 12,
        id: "move-work-items",
        text: Resources.MoveWorkItemTitle,
        iconProps: { iconName: "Assign" },
        groupKey: groupKey,
        onActivate: () => {
            const openDialogMethod = (pageContext: IVssPageContext, selectedItems: INamedFieldDataDictionary[]) => {
                openDialog(pageContext, onDismiss => (
                    <MoveToProjectDialog onDismiss={onDismiss} onModifyItems={onModifyItems} selectedItems={selectedItems} />
                ));
            };

            commonOpenDialogMethod(getSelectedItems, openDialogMethod, pageContext);
        }
    };
};

export const getNewBranchCommandMenuItem = (pageContext: IVssPageContext, getSelectedItems: () => INamedFieldDataDictionary[]) => {
    return {
        rank: 80,
        id: "new-branch",
        text: Resources.NewBranch,
        iconProps: { iconName: "OpenSource" },
        groupKey: "branch",
        onActivate: () => {
            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.executeCommandEx(
                "ms.vss-code-web.tags-version-dialog-actions-command",
                {
                    dependencies: ["ms.vss-code-web.version-dialogs-content", "ms.vss-code-web.versioncontrol-viewmodel-data-provider"],
                    serviceName: "IVersionDialogActionsService",
                    methodName: "createBranch"
                },
                {
                    initialWorkItemIds: getSelectedItems().map(resultItem => resultItem[CoreFieldRefNames.Id]),
                    allowChangingRepository: true
                }
            );
        }
    };
};

export const getRestoreCommandMenuItem = (
    pageContext: IVssPageContext,
    workItemIds: ObservableArray<string>,
    callback?: () => void,
    enabledByDefault?: boolean,
    rank?: number
) => {
    return {
        id: "restore-item",
        text: Resources.Restore,
        iconProps: {
            iconName: "RevToggleKey"
        },
        subtle: true,
        disabled: !enabledByDefault ?? workItemIds.length === 0,
        onActivate: () => {
            const onConfirm = async () => {
                openDialog(pageContext, onDismiss => (
                    <RecycleBinWorkItemsDialog
                        onDismiss={onDismiss}
                        workItemIds={workItemIds}
                        recycleBinRequestType={RecycleBinRequestType.Restore}
                        callBack={callback}
                        maximumAmountOfSimultaneousRequests={3}
                    />
                ));
            };
            showConfirmationDialog(pageContext, Resources.RestoreWorkItemMessage, Resources.Restore, Resources.RestoreWorkItemTitle, onConfirm);
        },
        rank: rank
    };
};

export const getPermanentlyDeleteCommandMenuItem = (
    pageContext: IVssPageContext,
    projectId: string,
    workItemIds: ObservableArray<string>,
    callback?: () => void,
    enabledByDefault?: boolean,
    rank?: number
) => {
    return {
        id: "permanently-delete-item",
        text: Resources.PermanentlyDelete,
        iconProps: {
            iconName: "Cancel",
            className: "error-text"
        },
        subtle: true,
        disabled: !enabledByDefault ?? workItemIds.length === 0,
        onActivate: () => {
            const onConfirm = async () => {
                openDialog(pageContext, onDismiss => (
                    <RecycleBinWorkItemsDialog
                        onDismiss={onDismiss}
                        workItemIds={workItemIds}
                        recycleBinRequestType={RecycleBinRequestType.Destroy}
                        callBack={callback}
                        maximumAmountOfSimultaneousRequests={10}
                    />
                ));
            };
            showConfirmationDialog(
                pageContext,
                Resources.PermanentlyDeleteMessage,
                Resources.PermanentlyDelete,
                Resources.PermanentlyDeleteTitle,
                onConfirm
            );
        },
        rank: rank
    };
};

function commonOpenDialogMethod(
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    openDialogMethod: (pageContext: IVssPageContext, items: INamedFieldDataDictionary[]) => void,
    pageContext: IVssPageContext
): void {
    const selectedItems = getSelectedItems();
    if (selectedItems instanceof Promise) {
        selectedItems
            .then(items => openDialogMethod(pageContext, items))
            .catch(error => getBoardsMessagesService(pageContext).showMessage(getErrorMessage(error)));
    } else {
        openDialogMethod(pageContext, selectedItems);
    }
}

export function getMoveToPositionDialog<T>(pageContext: IVssPageContext, items: T[], onMoveToPosition?: (position: number) => void, rank?: number) {
    return {
        disabled: false,
        groupKey: "manage-work-item",
        id: "move-item-to-position",
        onActivate: () => {
            openDialog(pageContext, onDismiss => <MoveToPositionDialog items={items} onDismiss={onDismiss} onMoveToPosition={onMoveToPosition} />);
        },
        rank,
        subtle: true,
        text: Resources.MoveToPositionContentMenuItem,
        iconProps: { iconName: "NumberedListText" }
    };
}

export const getUnassignedMenuItem = (
    pageContext: IVssPageContext,
    onActivateAction?: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean | void
): IMenuItem => {
    const unassignedIdentityRef = getUnassignedIdentityRef(pageContext);
    const imageUrl = getIdentityImageUrl(pageContext, unassignedIdentityRef);
    const unAssignItem: IMenuItem = {
        id: "_Unassigned_",
        text: Resources.Unassigned,
        iconProps: {
            render: () => {
                return (
                    <Persona
                        size={PersonaSize.size24}
                        className={"identity-avatar"}
                        role="presentation"
                        identity={{
                            displayName: Resources.Unassigned,
                            imageUrl: imageUrl,
                            originDirectory: "",
                            originId: "",
                            entityId: "",
                            entityType: "",
                        } as IIdentity}
                    />
                );
            }
        },
        onActivate: onActivateAction
    };
    return unAssignItem;
};

export const bulkUpdateWorkItems = async (
    pageContext: IVssPageContext,
    idsToUpdate: IWorkItem[] | number[],
    changesMap: Map<string, IWorkItemFieldChange[]>,
    optionalParamaters?: {
        onModifyItems?: (items: IWorkItem[]) => void | Promise<void>,
        useProcessDialog?: boolean,
        selection?: ListSelection,
        useChangeIterationPerfomanceOptimization?: boolean
    }
) => {
    const { onModifyItems, useProcessDialog, selection } = optionalParamaters ?? {};
    if (!useProcessDialog) {
        const bulkUpdateResult = await performBulkUpdate(pageContext, idsToUpdate, changesMap);
        onModifyItems && onModifyItems(bulkUpdateResult);
        return;
    }

    if (!idsToUpdate || idsToUpdate.length === 0) {
        return
    }

    const numberArrayInput = typeof idsToUpdate[0] === "number";
    const workItemsArray = numberArrayInput ? [] : idsToUpdate as IWorkItem[];
    const workItemIds = numberArrayInput
        ? idsToUpdate as number[]
        : workItemsArray.map(workItem => workItem.id);

    if (!workItemIds || workItemIds.length === 0) {
        return;
    }

    const selectionCopy = selection
        ? selection.value.map(item => ({ ...item }))
        : [];

    const useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
        pageContext,
        FeatureFlags.UseChangeIterationPerfomanceOptimization,
        false
    );

    await new Promise(resolve => {
        openDialog(pageContext, onDismiss => (
            <ProcessWorkItemsDialog
                onDismiss={() => {
                    onDismiss();
                    resolve(undefined);
                }}
                workItemIds={workItemIds}
                processWorkItems={async workItemIds => {
                    const batchItems = numberArrayInput ? workItemIds :
                        workItemIds.map(workItemId => {
                            return workItemsArray.find(item => item.id === workItemId);
                        }).filter(item => item !== undefined) as IWorkItem[];

                    const bulkUpdateResult = await performBulkUpdate(pageContext, batchItems, changesMap);
                    onModifyItems && await onModifyItems(bulkUpdateResult);
                    return true;
                }}
                maximumAmountOfSimultaneousRequests={1}
                callBack={() => {
                    if (selection && selectionCopy.length > 0) {
                        if (useChangeIterationPerfomanceOptimization) {
                            restoreSelection(selectionCopy, selection);
                        } else {
                            for (const range of selectionCopy) {
                                selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1);
                            }
                        }
                    }
                }}
            />
        ));
    });
}


export const onAssignToItemActivated = (
    pageContext: IVssPageContext,
    getSelectedItems: () => INamedFieldDataDictionary[],
    identity: WorkItemIdentityRef | undefined,
    onModifyItems?: (items: IWorkItem[]) => void | Promise<void>,
    getWorkItems?: (idsToUpdate: number[]) => IWorkItem[] | undefined,
    useProcessDialog?: boolean,
    selection?: ListSelection
) => {
    const items = getSelectedItems();
    const changesMap = new Map<string, IWorkItemFieldChange[]>();
    const changesList: IWorkItemFieldChange[] = [];

    if (identity) {
        changesList.push({ fieldName: CoreFieldRefNames.AssignedTo, value: identity });
    } else {
        changesList.push({ fieldName: CoreFieldRefNames.AssignedTo, value: undefined });
    }

    changesMap.set(BulkUpdateConstants.AllTypes, changesList);
    const idsToUpdate = items.map(fields => Number(fields[CoreFieldRefNames.Id])).filter(v => v);
    const itemsToUpdate = getWorkItems?.(idsToUpdate);
    bulkUpdateWorkItems(pageContext, itemsToUpdate ?? idsToUpdate, changesMap, { onModifyItems, useProcessDialog, selection });
};

export const onChangeActivityActivated = (
    pageContext: IVssPageContext,
    getSelectedItems: () => INamedFieldDataDictionary[],
    activity: string | undefined,
    activityFieldReferenceName: string,
    onModifyItems?: (items: IWorkItem[]) => void,
    useProcessDialog?: boolean,
    selection?: ListSelection
) => {
    const changesMap = new Map<string, IWorkItemFieldChange[]>();
    const changesList: IWorkItemFieldChange[] = [];
    if (activity) {
        changesList.push({ fieldName: activityFieldReferenceName, value: activity });
    } else {
        changesList.push({ fieldName: activityFieldReferenceName, value: undefined });
    }
    changesMap.set(BulkUpdateConstants.AllTypes, changesList);
    const idsToUpdate = getSelectedItems()
        .map(fields => Number(fields[CoreFieldRefNames.Id]))
        .filter(v => v);
    bulkUpdateWorkItems(pageContext, idsToUpdate, changesMap, { onModifyItems, useProcessDialog, selection });
};

export const onCopyHTML = async (
    pageContext: IVssPageContext,
    getSelectedItems: () => Promise<INamedFieldDataDictionary[]> | INamedFieldDataDictionary[],
    columns: IColumn[]
) => {
    try {
        const selectedItems = await getSelectedItems();

        if (selectedItems.length === 0 || columns.length === 0 || (selectedItems.length === 1 && selectedItems[0][CoreFieldRefNames.Id] < 0)) {
            return;
        }

        const decodedItems = await decodeSelectedItems(pageContext, selectedItems);
        const div = createHiddenDiv();
        const htmlString = getHtmlForSelection(pageContext, columns, decodedItems);
        div.innerHTML = htmlString;
        copyToClipboard(div);
        document.body.removeChild(div);
    } catch (error) {
        getBoardsMessagesService(pageContext).showMessage(getErrorMessage(error));
    }
};

const decodeSelectedItems = async (pageContext: IVssPageContext, selectedItems: INamedFieldDataDictionary[]): Promise<INamedFieldDataDictionary[]> => {
    const useNewFieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false);

    let workItemFieldService: IWorkItemFieldService | undefined;
    let workItemFieldService2: IWorkItemFieldService2 | undefined;
    if (useNewFieldService) {
        workItemFieldService2 = getWorkItemFieldService2(pageContext);
        if (!workItemFieldService2) {
            return selectedItems;
        }
        await workItemFieldService2.loadFields();
    } else {
        workItemFieldService = getWorkItemFieldService(pageContext);
        if (!workItemFieldService) {
            return selectedItems;
        }
    }


    const result = selectedItems.map(item => {
        let newItem: INamedFieldDataDictionary | undefined = undefined;
        for (const fieldRefName of Object.keys(item)) {
            const fieldType = useNewFieldService
                ? workItemFieldService2?.getFieldByReferenceName(fieldRefName)?.type
                : workItemFieldService?.getFields()?.find(field => field.referenceName === fieldRefName)?.type;
            if ((fieldType === FieldType.Html) && item[fieldRefName]) {
                if (!newItem) {
                    //allocate new memory only in case we need to decode html
                    newItem = Object.assign({}, item);
                }

                //modifies copies of the original item, so we don't need to worry about modifying the original item
                newItem[fieldRefName] = htmlDecode(item[fieldRefName]);
            } else if (newItem) {
                newItem[fieldRefName] = item[fieldRefName];
            }
        }
        return newItem ?? item;
    });

    return result;
}

export const onCopyLink = (pageContext: IVssPageContext, getSelectedItems: () => INamedFieldDataDictionary[]): void => {
    const selectedItem = getSelectedItems()[0];
    const project = selectedItem[CoreFieldRefNames.TeamProject];
    const url = getWorkItemUrl(pageContext, selectedItem[CoreFieldRefNames.Id], project);
    const workItemUrl = window.location.origin + url;
    copyToClipboard(String(workItemUrl));
};

export function getWorkItemUrl(pageContext: IVssPageContext, workItemId: number, projectName: string): string {
    const workItemFormRoute = "ms.vss-work-web.new-work-item-form-route";
    const routeValues = {
        project: projectName || "",
        id: workItemId.toString()
    };
    return routeUrl(pageContext, workItemFormRoute, routeValues);
}

export function getWorkItemAbsoluteUrl(pageContext: IVssPageContext, workItemId: number, projectName: string): string {
    const url = getWorkItemUrl(pageContext, workItemId, projectName);
    const workItemUrl = window.location.origin + url;
    return workItemUrl;
}

function openDialog(pageContext: IVssPageContext, callout: (onDismiss: () => void) => JSX.Element): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => callout(onDismiss));
}

const renderIterationCell = (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails) => {
    if (menuCell === MenuCell.PrimaryText) {
        return (
            <div id={getSafeId(menuItem.id + "-text")} className="bolt-menuitem-cell-content bolt-menuitem-cell-text flex-column">
                <Tooltip overflowOnly={true} text={menuItem.text}>
                    <div className="text-ellipsis">{menuItem.text}</div>
                </Tooltip>
                <Tooltip overflowOnly={true} text={menuItem.data.iterationPath}>
                    <div className="bolt-menuitem-cell-secondary text-ellipsis">{menuItem.data.iterationPath}</div>
                </Tooltip>
            </div>
        );
    }

    return undefined;
};

export const setIterationItems = (teamSettings: ITeamSettings | undefined, updateIterations: (index: number, iteration?: IIterationData) => void) => {
    const menuItems = new ObservableArray<IMenuItem>();

    if (!teamSettings || !teamSettings.backlogIteration) {
        menuItems.push({
            id: "backlog-menu-item",
            text: Resources.MissingTeamSettingsError,
            iconProps: { iconName: "icon-warning" }
        });
    } else {
        menuItems.push({
            id: "backlog-menu-item",
            data: { index: 0, id: teamSettings.backlogIteration.nodeId, iterationPath: teamSettings.backlogIteration.friendlyPath },
            groupKey: "backlog-and-current",
            iconProps: { iconName: "Backlog" },
            onActivate: () => {
                updateIterations(0, teamSettings.backlogIteration);
            },
            renderMenuCell: renderIterationCell,
            text: Resources.MoveToIteration_Backlog
        });
        if (teamSettings?.currentIteration) {
            menuItems.push({
                id: "current-iteration-menu-item",
                data: { index: 1, id: teamSettings.currentIteration.nodeId, iterationPath: teamSettings.currentIteration.friendlyPath },
                groupKey: "backlog-and-current",
                iconProps: { iconName: "TriangleSolidRight12" },
                onActivate: () => {
                    updateIterations(1, teamSettings.currentIteration);
                },
                renderMenuCell: renderIterationCell,
                text: format(Resources.MoveToIteration_Current, teamSettings.currentIteration.displayText)
            });
        }

        if (teamSettings.futureIterations && teamSettings.futureIterations.length > 0) {
            menuItems.push({
                id: "header-backlog-item",
                itemType: MenuItemType.Header,
                text: Resources.FutureIterations
            });
            for (const [i, futureIteration] of teamSettings.futureIterations.entries()) {
                menuItems.push({
                    id: "future-backlog-item",
                    data: { index: 2 + i, id: futureIteration.nodeId, iterationPath: futureIteration.friendlyPath },
                    onActivate: () => {
                        updateIterations(2 + i, futureIteration);
                    },
                    renderMenuCell: renderIterationCell,
                    text: futureIteration.displayText
                });
            }
        }
    }

    return menuItems;
};

function createHiddenDiv() {
    const div = document.createElement("div");
    div.classList.add("visually-hidden");
    div.setAttribute("aria-hidden", "true");
    div.setAttribute("role", "presentation");
    div.setAttribute("style", "{color: inherit; background-color: inherit;}");
    document.body.appendChild(div);

    return div;
}

const getHtmlForSelection = (pageContext: IVssPageContext, columns: IColumn[], selectedItems: INamedFieldDataDictionary[]) => {
    const headerRows = columns.map(c => `<th style="${COLUMN_STYLE}">${htmlEncode(c.text!)}</th>`);
    const tableHeader = `<thead style="${HEADER_STYLE}"><tr>${headerRows.join("")}</tr></thead>`;
    const tableRows = selectedItems.map((r, rowIndex) => {
        const innerRows = columns.map((c, index) => {
            let text = r[c.name ?? index];
            if (c.name?.toUpperCase() !== CoreFieldRefNames.Id.toUpperCase() && c.name?.toUpperCase() !== CoreFieldRefNames.Parent.toUpperCase()) {
                text = htmlEncode(convertValueToDisplayString(r[c.name ?? index]));
            } else if (c.isIdentity) {
                // get the display name of the identity ref
                text = htmlEncode(convertPotentialIdentityRefFromFieldValue(pageContext, text));
            } else {
                // ID  and Parent column should link to the item
                // text is work item ID or Parent ID
                const url = getWorkItemEditUrl(pageContext, text).url;
                text = `<a href="${url}" style="color:#106ebe" target="_blank">${text}</a>`;
            }

            if (text === "") {
                text = "&nbsp";
            }
            return `<td style="${COLUMN_STYLE}">${text}</td>`;
        });
        const rowStyle = rowIndex % 2 ? ROW_ALT_BACKGROUND_COLOR : ROW_BACKGROUND_COLOR;

        return `<tr style="${rowStyle}">${innerRows.join("")}</tr>`;
    });
    const tableBody = `<tbody style="${FONT_COLOR}">${tableRows.join("")}</tbody>`;
    return `<div><table border="0" cellpadding="0" cellspacing="0" style="${TABLE_STYLE}">${tableHeader}${tableBody}</table></div>`;
};

/**
 * HTML formatting constants
 */
const HEADER_BACKGROUND_COLOR = "background-color: #106EBE;";
const HEADER_COLOR = "color: white;";
const BACKGROUND_COLOR = "background-color: inherit;";
const FONT_SIZE = "font-size: 11pt;";
const FONT_FAMILY = "font-family: Calibri, sans-serif;";
const FONT_COLOR = "color: inherit;";
const BORDER_COLLAPSE = "border-collapse: collapse;";
const COLUMN_BORDER = "border: 1px solid white;";
const COLUMN_VERTICAL_ALIGN = "vertical-align: top;";
const COLUMN_PADDING = "padding: 1.45pt .05in;";
const ROW_BACKGROUND_COLOR = "";
const ROW_ALT_BACKGROUND_COLOR = "background-color: rgb(124, 124, 124, 0.5)";
const COLUMN_STYLE = COLUMN_BORDER + COLUMN_VERTICAL_ALIGN + COLUMN_PADDING;
const HEADER_STYLE = HEADER_BACKGROUND_COLOR + HEADER_COLOR;
const TABLE_STYLE = FONT_FAMILY + FONT_SIZE + FONT_COLOR + BORDER_COLLAPSE + BACKGROUND_COLOR;
